import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const BrandingContext = createContext({
    appName: "Steeltooth",
    logoUrl: "",
    videoUploadUrl: "",
    videoAnalyzeUrl: "",
    videoEstimateUrl: "",
    ready: false,
    refresh: () => {},
});

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Fetches the app-wide branding (name + logo + hero videos) from the
 * public ``/api/branding`` endpoint. Re-usable via ``useBranding()``.
 * Cached for the session so each route change doesn't re-hit the API.
 *
 * Also syncs ``document.title`` so the browser tab updates live when
 * the superadmin changes the name in the admin panel.
 */
export function BrandingProvider({ children }) {
    const [appName, setAppName] = useState("Steeltooth");
    const [logoUrl, setLogoUrl] = useState("");
    // iter-62: three independent hero video slots.
    const [videoUploadUrl, setVideoUploadUrl] = useState("");
    const [videoAnalyzeUrl, setVideoAnalyzeUrl] = useState("");
    const [videoEstimateUrl, setVideoEstimateUrl] = useState("");
    const [ready, setReady] = useState(false);

    const refresh = async () => {
        try {
            const { data } = await axios.get(`${API_BASE}/api/branding`);
            if (data?.app_name) setAppName(data.app_name);
            setLogoUrl(data?.logo_url || "");
            setVideoUploadUrl(data?.video_upload_url || "");
            setVideoAnalyzeUrl(data?.video_analyze_url || "");
            setVideoEstimateUrl(data?.video_estimate_url || "");
            setReady(true);
        } catch {
            setReady(true); // don't block rendering on a failed branding fetch
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    useEffect(() => {
        if (appName) document.title = `${appName} — Takeoff & Estimating`;
    }, [appName]);

    return (
        <BrandingContext.Provider
            value={{
                appName,
                logoUrl,
                videoUploadUrl,
                videoAnalyzeUrl,
                videoEstimateUrl,
                ready,
                refresh,
            }}
        >
            {children}
        </BrandingContext.Provider>
    );
}

export const useBranding = () => useContext(BrandingContext);
